<div class="monitor-shell">
  <section class="hero-band">
    <div class="hero-copy">
      <div class="eyebrow">Bess</div>
      <h1>{{ pageTitle() }}</h1>
      <p>Monitor DAM and RTM plant bids from one screen. Plant name is used across filters and the monitor table.</p>
    </div>
    <div class="hero-meta">
      <span>Home / {{ pageTitle() }}</span>
    </div>
  </section>

  <section class="toolbar-card">
    <div class="toolbar-grid">
      <label>
        <span>Exchange</span>
        <select [ngModel]="selectedExchange()" (ngModelChange)="setExchange($event)">
          <option *ngFor="let exchange of exchanges" [value]="exchange">{{ exchange }}</option>
        </select>
      </label>

      <label>
        <span>Trading Date</span>
        <div class="field-with-icon">
          <input type="date" [ngModel]="selectedDate()" (ngModelChange)="setDate($event)" />
          <lucide-icon [img]="CalendarRangeIcon" [size]="18"></lucide-icon>
        </div>
      </label>

      <label>
        <span>Product</span>
        <select [ngModel]="product()" (ngModelChange)="setProduct($event)">
          <option value="RTM">RTM</option>
          <option value="DAM">DAM</option>
        </select>
      </label>

      <label>
        <span>Session</span>
        <select [ngModel]="selectedSession()" (ngModelChange)="setSession($event)">
          <option *ngFor="let session of sessionOptions()" [value]="session.value">{{ session.label }}</option>
        </select>
      </label>

      <div class="action-cell">
        <button type="button" class="primary-btn" [disabled]="loading()" (click)="applyFilters()">GO</button>
        <button type="button" class="danger-btn" [disabled]="loading()" (click)="resetFilters()">RESET</button>
      </div>

      <label>
        <span>Plant Name</span>
        <div class="field-with-icon">
          <input type="text" placeholder="Search plant" [ngModel]="plantQuery()" (ngModelChange)="setPlantQuery($event)" />
          <lucide-icon [img]="SearchIcon" [size]="18"></lucide-icon>
        </div>
      </label>

      <label>
        <span>Select IEX User</span>
        <select [ngModel]="iexUser()" (ngModelChange)="setIexUser($event)">
          <option *ngFor="let user of iexUsers" [value]="user">{{ user }}</option>
        </select>
      </label>

      <div class="time-left">
        <small>Time Left</small>
        <strong>{{ timeLeft() }}</strong>
      </div>
    </div>
  </section>

  <section class="exchange-grid">
    <article class="exchange-card" *ngFor="let exchange of exchanges" [class.exchange-card-active]="exchange === selectedExchange()">
      <div class="exchange-head">
        <div>
          <h2>{{ exchange }}</h2>
          <p>{{ exchange === 'IEX' ? pageTitle() : 'Exchange feed pending' }}</p>
        </div>
        <lucide-icon [img]="BatteryChargingIcon" [size]="20"></lucide-icon>
      </div>

      <div class="exchange-stats">
        <div class="stat-chip stat-chip-highlight">Submitted: {{ exchangeSummaries()[exchange].submitted }}</div>
        <div class="stat-chip">Cancelled/Deleted: {{ exchangeSummaries()[exchange].cancelled }}</div>
        <div class="stat-chip">Re-Submitted: {{ exchangeSummaries()[exchange].resubmitted }}</div>
        <div class="stat-chip">Approved: {{ exchangeSummaries()[exchange].approved }}</div>
        <div class="stat-chip">No Funds: {{ exchangeSummaries()[exchange].noFunds }}</div>
        <div class="stat-chip">No Bid: {{ exchangeSummaries()[exchange].noBid }}</div>
        <div class="stat-chip">Pending: {{ exchangeSummaries()[exchange].pending }}</div>
        <div class="stat-chip">Rejected: {{ exchangeSummaries()[exchange].rejected }}</div>
        <div class="stat-chip">Failed: {{ exchangeSummaries()[exchange].failed }}</div>
        <div class="stat-chip stat-chip-total">Total: {{ exchangeSummaries()[exchange].total }}</div>
      </div>
    </article>
  </section>

  <section class="table-card">
    <div class="table-head">
      <div>
        <h2>{{ pageTitle() }} Overview</h2>
        <p>Current block {{ currentBlock() }} ({{ currentBlockLabel() }})</p>
      </div>
      <button type="button" class="ghost-btn" [disabled]="loading()" (click)="applyFilters()">
        <lucide-icon [img]="RefreshCcwIcon" [size]="16"></lucide-icon>
        <span>{{ loading() ? 'Refreshing...' : 'Refresh' }}</span>
      </button>
    </div>

    <div class="empty-state" *ngIf="loading()">Loading bid monitor data...</div>
    <div class="empty-state" *ngIf="!loading() && monitorRows().length === 0">No bid monitor rows available for the selected filters.</div>

    <div class="table-wrap" *ngIf="!loading() && monitorRows().length > 0">
      <table>
        <thead>
          <tr>
            <th>S.No.</th>
            <th>Plant Name</th>
            <th>Balance (Rs.)</th>
            <th>Portfolio No.</th>
            <th>Exchange</th>
            <th>Session</th>
            <th>Bid Nature</th>
            <th>Bid Quantum (MWh)</th>
            <th>Status</th>
            <th>Exchange Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of monitorRows()">
            <td>{{ row.serial }}</td>
            <td class="plant-name">
              <strong>{{ row.plantName }}</strong>
              <small>Market Price: {{ row.marketPrice | number:'1.2-2' }}</small>
            </td>
            <td>
              <span class="balance-pill" [ngClass]="balanceClass(row)">{{ row.balanceRs | number:'1.2-2' }}</span>
            </td>
            <td>{{ row.portfolioNo }}</td>
            <td>{{ row.exchange }}</td>
            <td>{{ row.session }}</td>
            <td>
              <span class="nature-pill" [ngClass]="actionClass(row.bidNature)">{{ row.bidNature }}</span>
            </td>
            <td>S: {{ row.bidQuantum | number:'1.2-2' }}</td>
            <td>
              <span class="status-pill" [ngClass]="statusClass(row.status)">{{ row.status }}</span>
            </td>
            <td>
              <span class="status-pill" [ngClass]="statusClass(row.exchangeStatus)">{{ row.exchangeStatus }}</span>
            </td>
            <td>
              <div class="table-actions">
                <button type="button" class="icon-btn view-btn" (click)="previewRow(row)" title="View">
                  <lucide-icon [img]="EyeIcon" [size]="16"></lucide-icon>
                </button>
                <button type="button" class="icon-btn edit-btn" (click)="editRow(row)" title="Edit">
                  <lucide-icon [img]="PencilIcon" [size]="16"></lucide-icon>
                </button>
                <button type="button" class="icon-btn copy-btn" (click)="cloneRow(row)" title="Copy">
                  <lucide-icon [img]="FilesIcon" [size]="16"></lucide-icon>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</div>
